import { useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import moment from "moment-timezone";
import { Box, Tooltip, Typography } from "@mui/material";
import {
  getConsDashboardAnalyticsApi,
} from "../../services/consultantManagement";
import { toastMessage } from "../../utils/toastMessage";

const CONSULTATION_METRIC_INFO = {
  attempt: {
    title: "Attempt",
    total: {
      description:
        "Total resolved private-call requests. Every attempt belongs to exactly one outcome: Completed, Missed, or Canceled. Query records are excluded.",
      example: "Total Attempt = Completed + Missed + Canceled.",
    },
    unique: {
      description:
        "Different customers who made at least one private-call request. Query records are excluded and repeated private-call attempts count once.",
      example: "3 attempts from 2 customers → Unique Attempt = 2.",
    },
  },
  missed: {
    title: "Missed",
    total: {
      description:
        "Total sessions that this consultant could not attend. Repeated missed sessions are counted separately.",
      example: "Consultant misses the same customer's call twice → Total Missed = 2.",
    },
    unique: {
      description:
        "Different customers with at least one canceled session carrying a consultant-missed signal. A customer may also appear in Unique Completed or Unique Canceled from another session.",
      example: "Unique outcome counts overlap and must not be added together.",
    },
  },
  missedChat: {
    title: "Missed chat",
    total: { description: "Missed consultations classified as chat.", example: "Missed Chat + Missed Call = Total Missed." },
  },
  missedCall: {
    title: "Missed call",
    total: { description: "Missed consultations classified as audio/video/call.", example: "Missed Chat + Missed Call = Total Missed." },
  },
  missedChatOnly: {
    title: "Missed chat only",
    unique: { description: "Customers with a missed chat but no missed call in the selected period.", example: "The three missed-mode buckets add to Unique Missed." },
  },
  missedCallOnly: {
    title: "Missed call only",
    unique: { description: "Customers with a missed call but no missed chat in the selected period.", example: "Each missed customer belongs to one bucket." },
  },
  missedChatAndCall: {
    title: "Missed chat & call both",
    unique: { description: "Customers who had both a missed chat and a missed call in the selected period.", example: "This overlap is counted once in Unique Missed." },
  },
  completed: {
    title: "Completed",
    total: {
      description:
        "Total successfully completed sessions with used_for exactly equal to private_call. Query, live-event, session, empty and missing used_for values are excluded.",
      example: "Customer completes chat, then later completes a call → Total Completed = 2.",
    },
    unique: {
      description:
        "Different customers who completed at least one private-call session. A customer may also have separate missed or canceled sessions.",
      example: "Use the completed chat/call-only/both buckets when an additive unique breakdown is required.",
    },
  },
  canceled: {
    title: "Canceled",
    total: {
      description:
        "Total sessions canceled by the customer, admin, or system.",
      example: "Customer cancels chat before connecting → counts here, not in Missed.",
    },
    unique: {
      description:
        "Different customers with at least one non-missed canceled session. A customer may also appear in another outcome from another session.",
      example: "Unique outcome counts overlap and must not be added together.",
    },
  },
  canceledChat: {
    title: "Canceled chat",
    total: { description: "Canceled consultations classified as chat.", example: "Canceled Chat + Canceled Call = Total Canceled." },
  },
  canceledCall: {
    title: "Canceled call",
    total: { description: "Canceled consultations classified as audio/video/call.", example: "Canceled Chat + Canceled Call = Total Canceled." },
  },
  canceledChatOnly: {
    title: "Canceled chat only",
    unique: { description: "Customers with a canceled chat but no canceled call in the selected period.", example: "The three canceled-mode buckets add to Unique Canceled." },
  },
  canceledCallOnly: {
    title: "Canceled call only",
    unique: { description: "Customers with a canceled call but no canceled chat in the selected period.", example: "Each canceled customer belongs to one bucket." },
  },
  canceledChatAndCall: {
    title: "Canceled chat & call both",
    unique: { description: "Customers who had both a canceled chat and a canceled call in the selected period.", example: "This overlap is counted once in Unique Canceled." },
  },
  chat: {
    title: "Chat",
    total: {
      description:
        "Total private-call records classified as chat. Query records are excluded.",
    },
    unique: {
      description:
        "How many different customers had at least one chat consultation with this consultant.",
      example: "Same user takes 5 chats → Unique Chat = 1.",
    },
  },
  call: {
    title: "Call",
    total: {
      description:
        "Total private-call records classified as call. Query records are excluded.",
    },
    unique: {
      description:
        "How many different customers had at least one call consultation with this consultant.",
      example: "Same customer takes 3 calls → Unique Call = 1.",
    },
  },
  chatOnly: {
    title: "Chat only",
    unique: { description: "Different customers who attempted chat but never attempted call in the selected period.", example: "Chat only + Call only + Chat & Call both = Unique Attempt." },
  },
  callOnly: {
    title: "Call only",
    unique: { description: "Different customers who attempted call but never attempted chat in the selected period.", example: "Each customer belongs to only one attempt-mode bucket." },
  },
  chatAndCall: {
    title: "Chat & call both",
    unique: { description: "Different customers who attempted both chat and call in the selected period.", example: "This separate overlap bucket prevents double-counting." },
  },
  completedChat: {
    title: "Completed chat",
    total: { description: "Completed consultations classified as chat.", example: "Completed Chat + Completed Call = Total Completed." },
  },
  completedCall: {
    title: "Completed call",
    total: { description: "Completed consultations classified as audio/video/call.", example: "Completed Chat + Completed Call = Total Completed." },
  },
  completedChatOnly: {
    title: "Completed chat only",
    unique: { description: "Customers who completed chat but did not complete a call in the selected period.", example: "The three completed-mode buckets add to Unique Completed." },
  },
  completedCallOnly: {
    title: "Completed call only",
    unique: { description: "Customers who completed a call but did not complete chat in the selected period.", example: "Each completed customer belongs to one bucket." },
  },
  completedChatAndCall: {
    title: "Completed chat & call both",
    unique: { description: "Customers who completed both chat and call in the selected period.", example: "This overlap is counted once in Unique Completed." },
  },
};

const TIME_FILTER_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "hourly", label: "Hourly" },
  { value: "today", label: "Today" },
  { value: "last7days", label: "7 days" },
  { value: "last30days", label: "30 days" },
  { value: "custom", label: "Custom" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: moment.tz("Asia/Kolkata").startOf("day").hour(hour).format("h:00 A"),
}));
const END_HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => {
  const hour = index + 1;
  return {
    value: String(hour),
    label: hour === 24
      ? "12:00 AM (next day)"
      : moment.tz("Asia/Kolkata").startOf("day").hour(hour).format("h:00 A"),
  };
});

const getDateRangeForPreset = (preset, customStart, customEnd, hourlyDate, hourlyStartHour, hourlyEndHour) => {
  const tz = "Asia/Kolkata";
  if (preset === "all") return null;
  if (preset === "hourly" && hourlyDate && hourlyStartHour !== "" && hourlyEndHour !== "") {
    const start = moment.tz(hourlyDate, "YYYY-MM-DD", tz).hour(Number(hourlyStartHour)).startOf("hour");
    const end = moment.tz(hourlyDate, "YYYY-MM-DD", tz).hour(Number(hourlyEndHour)).startOf("hour");
    return {
      startDate: start.toISOString(),
      endDate: end.subtract(1, "millisecond").toISOString(),
    };
  }
  if (preset === "custom" && customStart && customEnd) {
    return {
      startDate: moment.tz(customStart, tz).startOf("day").toISOString(),
      endDate: moment.tz(customEnd, tz).endOf("day").toISOString(),
    };
  }
  const end = moment.tz(tz).endOf("day");
  let start;
  if (preset === "today") {
    start = moment.tz(tz).startOf("day");
  } else if (preset === "last7days") {
    start = moment.tz(tz).subtract(6, "days").startOf("day");
  } else if (preset === "last30days") {
    start = moment.tz(tz).subtract(29, "days").startOf("day");
  } else {
    return null;
  }
  return { startDate: start.toISOString(), endDate: end.toISOString() };
};

const formatCount = (value) => Number(value || 0).toLocaleString("en-IN");
const formatMoney = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatPct = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatRating = (value) => `${Number(value || 0).toFixed(2)} / 5`;
const dateInputValue = (date) => moment(date).tz("Asia/Kolkata").format("YYYY-MM-DD");
const ratioPct = (numerator, denominator) =>
  Number(denominator) > 0 ? (Number(numerator || 0) / Number(denominator)) * 100 : 0;

const HelpLabel = ({ label, description, example }) => {
  if (!description) return <div className="cons-ot-row-label">{label}</div>;
  return (
    <Tooltip
      arrow
      placement="top"
      enterDelay={250}
      title={
        <Box sx={{ maxWidth: 380, py: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            {description}
          </Typography>
          {example ? (
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: 1, display: "block", opacity: 0.92 }}
            >
              <strong>Example:</strong> {example}
            </Typography>
          ) : null}
        </Box>
      }
    >
      <div className="cons-ot-row-label cons-ot-row-label-help">{label}</div>
    </Tooltip>
  );
};

const Row = ({ label, value, hint, description, example }) => (
  <div className="cons-ot-row">
    <div>
      <HelpLabel label={label} description={description} example={example} />
      {hint ? <div className="cons-ot-row-hint">{hint}</div> : null}
    </div>
    <div className="cons-ot-row-value">{value}</div>
  </div>
);

const Card = ({ title, children }) => (
  <div className="cons-ot-card">
    {title ? <div className="cons-ot-card-title">{title}</div> : null}
    {children}
  </div>
);

const DashboardAnalytics = () => {
  const { id: userId } = useParams();
  const [searchParams] = useSearchParams();
  const alertDate = searchParams.get("alertDate");
  const todayIst = dateInputValue(moment.tz("Asia/Kolkata"));
  const useAlertDate = Boolean(alertDate && alertDate !== todayIst);
  const [dashboardData, setDashboardData] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeFilterPreset, setTimeFilterPreset] = useState(useAlertDate ? "custom" : "today");
  const [customStartDate, setCustomStartDate] = useState(useAlertDate ? alertDate : "");
  const [customEndDate, setCustomEndDate] = useState(useAlertDate ? alertDate : "");
  const [hourlyDate, setHourlyDate] = useState(() => dateInputValue(moment.tz("Asia/Kolkata")));
  const [hourlyStartHour, setHourlyStartHour] = useState(() => String(moment.tz("Asia/Kolkata").hour()));
  const [hourlyEndHour, setHourlyEndHour] = useState(() => String(Math.min(24, moment.tz("Asia/Kolkata").hour() + 1)));
  const [customerEndDate, setCustomerEndDate] = useState(() =>
    useAlertDate ? alertDate : dateInputValue(moment.tz("Asia/Kolkata"))
  );
  const [repeat90EndDate, setRepeat90EndDate] = useState(() =>
    useAlertDate ? alertDate : dateInputValue(moment.tz("Asia/Kolkata"))
  );

  const fetchDashboardAnalytics = useCallback(async (uid, dateRange, customerEnd, repeatEnd, signal) => {
    if (!uid) return;
    setLoading(true);
    try {
      const sendData = { consultantId: uid };
      if (dateRange?.startDate) sendData.startDate = dateRange.startDate;
      if (dateRange?.endDate) sendData.endDate = dateRange.endDate;
      if (customerEnd) {
        sendData.customerEndDate = moment.tz(customerEnd, "Asia/Kolkata").endOf("day").toISOString();
      }
      if (repeatEnd) {
        sendData.repeat90EndDate = moment.tz(repeatEnd, "Asia/Kolkata").endOf("day").toISOString();
      }
      const { data } = await getConsDashboardAnalyticsApi(sendData, { signal });
      if (signal?.aborted) return;
      if (data?.code === 200) {
        setDashboardData(data?.result || {});
      } else {
        setDashboardData({});
        toastMessage(data?.message, "error", "DashboardAnalytics");
      }
    } catch (error) {
      if (signal?.aborted || error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
      toastMessage("Failed to fetch analytics data", "error", "DashboardAnalytics");
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();
    const dateRange = getDateRangeForPreset(
      timeFilterPreset,
      customStartDate,
      customEndDate,
      hourlyDate,
      hourlyStartHour,
      hourlyEndHour
    );
    fetchDashboardAnalytics(userId, dateRange, customerEndDate, repeat90EndDate, controller.signal);
    return () => controller.abort();
  }, [userId, timeFilterPreset, customStartDate, customEndDate, hourlyDate, hourlyStartHour, hourlyEndHour, customerEndDate, repeat90EndDate, fetchDashboardAnalytics]);

  const stats = dashboardData?.consultationStats || {};
  const pair = (key) => ({
    total: Number(stats?.[key]?.total) || 0,
    unique: Number(stats?.[key]?.unique) || 0,
  });

  const totalConsultationCaseKeys = ["attempt", "chat", "call", "missed", "missedChat", "missedCall", "completed", "completedChat", "completedCall", "canceled", "canceledChat", "canceledCall"];
  const uniqueConsultationCaseKeys = ["attempt", "chatOnly", "callOnly", "chatAndCall", "missed", "missedChatOnly", "missedCallOnly", "missedChatAndCall", "completed", "completedChatOnly", "completedCallOnly", "completedChatAndCall", "canceled", "canceledChatOnly", "canceledCallOnly", "canceledChatAndCall"];

  const toRows = (kind) =>
    (kind === "total" ? totalConsultationCaseKeys : uniqueConsultationCaseKeys).map((key) => {
      const info = CONSULTATION_METRIC_INFO[key];
      const tooltip = info[kind];
      const isAttemptByMode = key === "chat" || key === "call";
      return {
        key: `${kind}-${key}`,
        label: isAttemptByMode
          ? `${kind === "total" ? "Total" : "Unique"} ${info.title.toLowerCase()} attempt`
          : kind === "unique" && ["chatOnly", "callOnly", "chatAndCall", "missedChatOnly", "missedCallOnly", "missedChatAndCall", "completedChatOnly", "completedCallOnly", "completedChatAndCall", "canceledChatOnly", "canceledCallOnly", "canceledChatAndCall"].includes(key)
            ? `Unique ${info.title.toLowerCase()}`
            : info.title,
        value: formatCount(pair(key)[kind]),
        description: tooltip.description,
        example: tooltip.example,
      };
    });

  const totalEarning = Number(dashboardData.totalGeneratedAmount ?? dashboardData.totalRevenueGenerated) || 0;
  const lifetimeEarning = Number(dashboardData.lifeTimeEarning) || 0;
  const refundAmount = Number(dashboardData.totalAmountRefunded) || 0;
  const payoutSummary = dashboardData.payoutSummary || {};
  const consultantEarningBeforeCharges = Number(payoutSummary.earningBeforeCharges) || 0;
  const paymentGatewayCharge = Number(payoutSummary.paymentGatewayCharge) || 0;
  const tdsCharge = Number(payoutSummary.tdsCharge) || 0;
  const consultantEarningAfterCharges = Number(payoutSummary.earningAfterCharges) || 0;
  const pgPercent = Number(payoutSummary.pgPercent ?? 2.5);
  const tdsPercent = Number(payoutSummary.tdsPercent ?? 10);
  const giftEarning = Number(dashboardData.giftEarning) || 0;
  const powerCustomers = Number(dashboardData.powerCustomers) || 0;
  const potentialPowerCustomers = Number(dashboardData.potentialPowerCustomers) || 0;
  const repeat90DayUsers = Number(dashboardData.repeat90DayUsers) || 0;
  const uniqueCompleted = pair("completed").unique;
  const cohortWindow = dashboardData.customerCohortWindow || {};
  const cohortWindowHint = cohortWindow.startDate && cohortWindow.endDate
    ? `${moment(cohortWindow.startDate).tz("Asia/Kolkata").format("DD MMM YYYY")} – ${moment(cohortWindow.endDate).tz("Asia/Kolkata").format("DD MMM YYYY")}`
    : "Last 6 months";
  const repeat90Window = dashboardData.repeat90DayWindow || {};
  const repeat90WindowHint = repeat90Window.startDate && repeat90Window.endDate
    ? `${moment(repeat90Window.startDate).tz("Asia/Kolkata").format("DD MMM YYYY")} – ${moment(repeat90Window.endDate).tz("Asia/Kolkata").format("DD MMM YYYY")}`
    : "Last 90 days";
  const conv = dashboardData.conversionMetrics || {};
  const convPair = (key) => conv[key] || { pct: 0, from: 0, to: 0 };
  const queryPair = {
    pct: Number(conv.queryConversionPct) || 0,
    from: Number(conv.queryAccepted) || 0,
    to: Number(conv.queryConverted) || 0,
  };
  const consult1to2 = convPair("consult1to2");
  const consult2to3 = convPair("consult2to3");
  const consult3to4 = convPair("consult3to4");
  const recharge1to2 = convPair("recharge1to2");
  const recharge2to3 = convPair("recharge2to3");
  const recharge3to4 = convPair("recharge3to4");
  const convHint = (item) => `${formatCount(item.to)} of ${formatCount(item.from)}`;

  const totalRows = [
    {
      key: "total-earning",
      label: "Total revenue",
      value: formatMoney(totalEarning),
      description: "Total money generated for the company from this consultant's consultations.",
      example: "User pays ₹100 → Total Revenue increases by ₹100.",
    },
    {
      key: "consultant-earning-before-charges",
      label: "Consultant earning (before TDS, PG)",
      value: formatMoney(consultantEarningBeforeCharges),
      description: "Consultant earning for the selected period before Payment Gateway and TDS deductions, using the same rules as Consultant Payout.",
      example: "Eligible wallet credits minus non-payout debits, including applicable wallet additions and refunds.",
    },
    {
      key: "payment-gateway-charge",
      label: `Payment gateway (${pgPercent}%)`,
      value: formatMoney(paymentGatewayCharge),
      description: "Payment Gateway deduction calculated on consultant earning.",
      example: `Consultant earning × ${pgPercent}%.`,
    },
    {
      key: "tds-charge",
      label: `TDS (${tdsPercent}%)`,
      value: formatMoney(tdsCharge),
      description: "TDS deduction calculated after deducting the Payment Gateway charge.",
      example: `(Consultant earning − PG) × ${tdsPercent}%.`,
    },
    {
      key: "consultant-earning-after-charges",
      label: "Consultant earning (after TDS, PG)",
      value: formatMoney(consultantEarningAfterCharges),
      description: "Final consultant earning after Payment Gateway and TDS deductions.",
      example: "Consultant earning − PG − TDS.",
    },
    {
      key: "lifetime-earning",
      label: "Lifetime earning",
      value: formatMoney(lifetimeEarning),
      description: "The consultant wallet's net lifetime earning across all time, including gift credits.",
      example: "Lifetime earning includes consultation earnings and gift earnings, minus applicable wallet debits.",
    },
    {
      key: "gift-earning",
      label: "Gift earning",
      value: formatMoney(giftEarning),
      description: "Gift credits received by this consultant in the selected dashboard period.",
      example: "With Today selected, this shows today's gift credits; Lifetime earning already includes gifts across all time.",
    },
    {
      key: "refund-amount",
      label: "Refund amount",
      value: formatMoney(refundAmount),
      description: "Money refunded to customers for this consultant's consultations.",
      example: "A ₹50 refund on a completed session → Refund amount increases by ₹50.",
    },
    ...toRows("total"),
  ];

  const uniqueRows = [
    {
      key: "power-customers",
      label: "Power customers",
      value: formatCount(powerCustomers),
      hint: cohortWindowHint,
      description: "Different customers who spent ₹1,500 or more on completed consultations with this consultant during the six months ending on the selected Power Customer end date.",
      example: "A customer's completed sessions total ₹1,500 or more in this date range → counted once as a Power Customer.",
    },
    {
      key: "potential-power-customers",
      label: "Potential power customers",
      value: formatCount(potentialPowerCustomers),
      hint: cohortWindowHint,
      description: "Different customers who spent at least ₹500 but less than ₹1,500 on completed consultations with this consultant during the six months ending on the selected Power Customer end date.",
      example: "A customer's completed sessions total ₹500–₹1,499.99 in this date range → counted once as a Potential Power Customer.",
    },
    {
      key: "repeat-90-day-users",
      label: "Repeat 90-day users",
      value: formatCount(repeat90DayUsers),
      hint: repeat90WindowHint,
      description: "Different users who completed at least two consultations with this consultant during the selected 90-day window.",
      example: "A user completes 2 or more consultations in the 90-day window → counted once as a Repeat 90-day User.",
    },
    {
      key: "power-to-unique-completed-ratio",
      label: "Power / unique completed",
      value: formatPct(ratioPct(powerCustomers, uniqueCompleted)),
      hint: `${formatCount(powerCustomers)} ÷ ${formatCount(uniqueCompleted)} × 100`,
      description: "Power customers as a percentage of customers who completed at least one consultation.",
      example: "50 Power Customers ÷ 200 Unique Completed × 100 = 25%.",
    },
    {
      key: "potential-to-unique-completed-ratio",
      label: "Potential power / unique completed",
      value: formatPct(ratioPct(potentialPowerCustomers, uniqueCompleted)),
      hint: `${formatCount(potentialPowerCustomers)} ÷ ${formatCount(uniqueCompleted)} × 100`,
      description: "Potential Power Customers as a percentage of customers who completed at least one consultation.",
      example: "100 Potential Power Customers ÷ 200 Unique Completed × 100 = 50%.",
    },
    {
      key: "power-to-potential-ratio",
      label: "Power / potential power ratio",
      value: formatPct(ratioPct(powerCustomers, potentialPowerCustomers)),
      hint: `${formatCount(powerCustomers)} ÷ ${formatCount(potentialPowerCustomers)} × 100`,
      description: "Power Customers as a percentage of Potential Power Customers.",
      example: "50 Power Customers ÷ 100 Potential Power Customers × 100 = 50%.",
    },
    {
      key: "avg-chat-rating",
      label: "Avg chat rating",
      value: formatRating(dashboardData.avgChatRating),
      description: "Average customer rating out of 5 for this consultant's chat consultations in the selected period.",
      example: `${Number(dashboardData.chatRatingCount) || 0} chat ratings. Example: ratings 5, 4, 5 → Avg chat rating = 4.67 / 5.`,
    },
    {
      key: "avg-call-rating",
      label: "Avg call rating",
      value: formatRating(dashboardData.avgCallRating),
      description: "Average customer rating out of 5 for this consultant's call consultations in the selected period.",
      example: `${Number(dashboardData.callRatingCount) || 0} call ratings. Example: ratings 4, 5 → Avg call rating = 4.50 / 5.`,
    },
    {
      key: "avg-total-rating",
      label: "Avg total rating",
      value: formatRating(dashboardData.avgTotalRating),
      description: "Average of all consultancy feedback ratings for this consultant in the selected range (chat and call together).",
      example: `${Number(dashboardData.totalRatingCount) || 0} total ratings. Example: 10 chat + 6 call ratings averaged together.`,
    },
    ...toRows("unique"),
  ];

  const conversionRows = [
    {
      key: "query-conversion",
      label: "Query conversion",
      value: formatPct(queryPair.pct),
      hint: convHint(queryPair),
      description: "Of users whose query this consultant accepted in the selected range, how many then recharged and completed their first paid consultation with the same consultant. 2nd step can happen after the range.",
      example: `${queryPair.to} of ${queryPair.from} query-accepted users converted. Example: query accepted → recharge → first paid consult with this consultant.`,
    },
    {
      key: "consult-1-2",
      label: "1st → 2nd consultation",
      value: formatPct(consult1to2.pct),
      hint: convHint(consult1to2),
      description: "Users who took their 1st paid consultation with this consultant (in the selected range), then recharged, then took a 2nd paid consultation with the same consultant. Without a recharge between 1st and 2nd, it is not a conversion.",
      example: `${consult1to2.to} of ${consult1to2.from} users. Example: 1st consult → recharge → 2nd consult with same astrologer.`,
    },
    {
      key: "recharge-1-2",
      label: "1st → 2nd recharge",
      value: formatPct(recharge1to2.pct),
      hint: convHint(recharge1to2),
      description: "Users who recharged after their 1st paid consult with this consultant (that recharge in the selected range), then took a 2nd consult with the same consultant, then recharged again. Sequence: 1st consult → 1st recharge → 2nd consult → 2nd recharge.",
      example: `${recharge1to2.to} of ${recharge1to2.from} users. Example: recharge after 1st consult, come back for 2nd consult, then recharge again.`,
    },
    {
      key: "consult-2-3",
      label: "2nd → 3rd consultation",
      value: formatPct(consult2to3.pct),
      hint: convHint(consult2to3),
      description: "Users who took their 2nd paid consultation with this consultant (in the selected range), then recharged, then took a 3rd paid consultation with the same consultant.",
      example: `${consult2to3.to} of ${consult2to3.from} users. Example: 2nd consult → recharge → 3rd consult with same astrologer.`,
    },
    {
      key: "recharge-2-3",
      label: "2nd → 3rd recharge",
      value: formatPct(recharge2to3.pct),
      hint: convHint(recharge2to3),
      description: "Users who recharged after their 2nd paid consult with this consultant (that recharge in the selected range), then took a 3rd consult with the same consultant, then recharged again.",
      example: `${recharge2to3.to} of ${recharge2to3.from} users. Example: 2nd consult → recharge → 3rd consult → recharge again.`,
    },
    {
      key: "consult-3-4",
      label: "3rd → 4th consultation",
      value: formatPct(consult3to4.pct),
      hint: convHint(consult3to4),
      description: "Users who took their 3rd paid consultation with this consultant (in the selected range), then recharged, then took a 4th paid consultation with the same consultant.",
      example: `${consult3to4.to} of ${consult3to4.from} users. Example: 3rd consult → recharge → 4th consult with same astrologer.`,
    },
    {
      key: "recharge-3-4",
      label: "3rd → 4th recharge",
      value: formatPct(recharge3to4.pct),
      hint: convHint(recharge3to4),
      description: "Users who recharged after their 3rd paid consult with this consultant (that recharge in the selected range), then took a 4th consult with the same consultant, then recharged again.",
      example: `${recharge3to4.to} of ${recharge3to4.from} users. Example: 3rd consult → recharge → 4th consult → recharge again.`,
    },
  ];

  const rangeLabel = TIME_FILTER_OPTIONS.find((item) => item.value === timeFilterPreset)?.label || "All time";

  return (
    <div className="cons-ot-panel">
      <div className="cons-ot-header">
        <div>
          <h5>Dashboard Analytics</h5>
          <div className="cons-ot-sub">{rangeLabel} · IST (Asia/Kolkata)</div>
        </div>
        <div className="cons-ot-ranges">
          {TIME_FILTER_OPTIONS.map((item) => (
            <button
              key={item.value}
              type="button"
              className={`cons-ot-range ${timeFilterPreset === item.value ? "active" : ""}`}
              onClick={() => setTimeFilterPreset(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {timeFilterPreset === "custom" ? (
        <div className="cons-ot-custom-dates">
          <input
            type="date"
            className="form-control"
            value={customStartDate}
            onChange={(e) => setCustomStartDate(e.target.value)}
          />
          <span>to</span>
          <input
            type="date"
            className="form-control"
            value={customEndDate}
            onChange={(e) => setCustomEndDate(e.target.value)}
          />
        </div>
      ) : null}

      {timeFilterPreset === "hourly" ? (
        <div className="cons-ot-custom-dates">
          <input
            type="date"
            className="form-control"
            aria-label="Hourly analytics date"
            value={hourlyDate}
            onChange={(e) => setHourlyDate(e.target.value)}
          />
          <select
            className="form-control"
            aria-label="Hourly analytics start hour"
            value={hourlyStartHour}
            onChange={(e) => {
              const nextStart = Number(e.target.value);
              setHourlyStartHour(e.target.value);
              if (Number(hourlyEndHour) <= nextStart) {
                setHourlyEndHour(String(Math.min(24, nextStart + 1)));
              }
            }}
          >
            {HOUR_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <span>to</span>
          <select
            className="form-control"
            aria-label="Hourly analytics end hour"
            value={hourlyEndHour}
            onChange={(e) => setHourlyEndHour(e.target.value)}
          >
            {END_HOUR_OPTIONS.filter((item) => Number(item.value) > Number(hourlyStartHour)).map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <span>IST · selected time range</span>
        </div>
      ) : null}

      {loading && !Object.keys(dashboardData).length ? (
        <div className="cons-ot-loading">Loading analytics…</div>
      ) : (
        <div className="cons-ot-grid cons-ot-grid-3 cons-ot-grid-equal">
          <Card title="Total">
            {totalRows.map((row) => (
              <Row key={row.key} {...row} />
            ))}
          </Card>
          <Card title="Unique">
            <div className="cons-ot-customer-range-label">
              Power customer end date <span>(previous 6 months · next 2 rows only)</span>
            </div>
            <div className="cons-ot-custom-dates cons-ot-customer-dates">
              <input
                type="date"
                className="form-control"
                aria-label="Power customer end date"
                value={customerEndDate}
                onChange={(e) => setCustomerEndDate(e.target.value)}
              />
            </div>
            <div className="cons-ot-customer-range-label">
              Repeat 90-day end date <span>(previous 90 days · Repeat row only)</span>
            </div>
            <div className="cons-ot-custom-dates cons-ot-customer-dates">
              <input
                type="date"
                className="form-control"
                aria-label="Repeat 90-day end date"
                value={repeat90EndDate}
                onChange={(e) => setRepeat90EndDate(e.target.value)}
              />
            </div>
            {uniqueRows.map((row) => (
              <Row key={row.key} {...row} />
            ))}
          </Card>
          <Card title="Conversion %">
            {conversionRows.map((row) => (
              <Row key={row.key} {...row} />
            ))}
          </Card>
        </div>
      )}
    </div>
  );
};

export default DashboardAnalytics;
